/* eslint-disable ember/no-controller-access-in-routes, warp-drive/no-legacy-request-patterns */
import { service } from '@ember/service';
import Route from '@ember/routing/route';
import { isEmpty } from '@ember/utils';
import { hash } from '@linc-technologies/linced-common/utils/promise';
import { mapBy } from '@linc-technologies/linced-common/utils/arrays';
import { loadPeople } from '@linc-technologies/linced-common/utils/load-people';

// billPayerIds gives the person ids of the learners' bill payers.
function* billPayerIds(people) {
  for (const person of people) {
    const contacts = person.get('learner.primaryContacts');
    if (!contacts) {
      continue;
    }
    for (const contact of contacts) {
      if (contact.billPayer) {
        yield contact.caregiverId;
      }
    }
  }
}

export default class IndexRoute extends Route {
  queryParams = {
    amount: {
      refreshModel: false,
    },
    name: {
      refreshModel: true,
    },
    personId: {
      refreshModel: true,
    },
  };

  // Services
  @service
  store;

  model(params) {
    if (!isEmpty(params.personId)) {
      return this.store.findRecord('person', params.personId).then((person) => {
        if (isEmpty(person)) {
          // No matching person; return an empty array.
          return [];
        }

        // Ensure any/all siblings are loaded, so that we can include accounts for only those who aren't archived.
        return loadPeople(
          this.store,
          person.get('learner.localSiblingIds') ?? [],
          {
            includeFormer: true,
          },
        ).then(() => {
          let siblings = person.get('learner.allSiblings');
          if (isEmpty(siblings)) {
            // No siblings - just return the person.
            return hash({
              people: [person],
              accounts: this.store.query('finance/account', {
                personId: person.id,
              }),
            });
          }

          // Retrieve siblings and them along with the requested person.
          let siblingIds = mapBy(siblings, 'personId');
          // Retrieve the requested person again, so that we still end up with an Ember Data record set.
          // This doesn't incur any extra round trips - just a little extra data returned.
          siblingIds.push(person.id);
          let people = JSON.stringify(siblingIds);
          return hash({
            people: this.store.query('person', { people, includeFormer: true }),
            accounts: this.store.query('finance/account', {
              personIds: people,
            }),
          });
        });
      });
    }

    return hash({
      people: this.store.query('person', { query: params.name }),
      accounts: [], // Not needed in this mode.
    });
  }

  // afterModel loads the bill payers that the results show.
  afterModel(model) {
    return loadPeople(this.store, billPayerIds(model.people ?? []));
  }

  resetController() {
    this.controller.resetForm();
  }
}
